/**
 * Data decimation utilities using Largest Triangle Three Buckets (LTTB) algorithm
 * Preserves visual accuracy while reducing data points for smooth visualization
 */

export interface DataPoint {
  x: number;
  y: number;
  [key: string]: any; // Allow additional properties
}

/**
 * Calculate triangle area for LTTB algorithm
 */
function calculateTriangleArea(a: DataPoint, b: DataPoint, c: DataPoint): number {
  return Math.abs((a.x - c.x) * (b.y - a.y) - (a.x - b.x) * (c.y - a.y));
}

/**
 * Largest Triangle Three Buckets (LTTB) decimation algorithm
 * Reduces data points while preserving visual characteristics
 *
 * @param data Array of data points with x, y coordinates
 * @param targetPoints Target number of points (default: 1000)
 * @returns Decimated array of data points
 */
export function decimateData<T extends DataPoint>(data: T[], targetPoints: number = 1000): T[] {
  if (data.length <= targetPoints) {
    return data;
  }

  if (targetPoints < 3) {
    return data.slice(0, targetPoints);
  }

  const bucketSize = (data.length - 2) / (targetPoints - 2);
  const decimated: T[] = [data[0]]; // Always keep first point

  for (let i = 1; i < targetPoints - 1; i++) {
    // Calculate bucket boundaries
    const bucketStart = Math.floor(i * bucketSize) + 1;
    const bucketEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, data.length - 1);

    // Average point in next bucket for triangle calculation
    let avgX = 0;
    let avgY = 0;
    const nextBucketStart = bucketEnd;
    const nextBucketEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, data.length - 1);

    for (let j = nextBucketStart; j < nextBucketEnd; j++) {
      avgX += data[j].x;
      avgY += data[j].y;
    }

    const avgPoint = {
      x: avgX / (nextBucketEnd - nextBucketStart),
      y: avgY / (nextBucketEnd - nextBucketStart),
    };

    // Find point with largest triangle area in current bucket
    let maxArea = 0;
    let selectedPoint = data[bucketStart];

    for (let j = bucketStart; j < bucketEnd; j++) {
      const area = calculateTriangleArea(
        { x: decimated[decimated.length - 1].x, y: decimated[decimated.length - 1].y },
        { x: data[j].x, y: data[j].y },
        avgPoint
      );

      if (area > maxArea) {
        maxArea = area;
        selectedPoint = data[j];
      }
    }

    decimated.push(selectedPoint);
  }

  decimated.push(data[data.length - 1]); // Always keep last point
  return decimated;
}

/**
 * Convert telemetry data to visualization format and decimate
 */
export function decimateTelemetryData(
  data: Array<{ timestamp: number; gateAngle: number; gateForceX: number; gateAngleVel: number }>,
  targetPoints: number = 1000
): Array<{ x: number; y: number; timestamp: number; force: number; velocity: number }> {
  // Convert to DataPoint format
  const converted = data.map((point) => ({
    x: point.gateAngle,
    y: point.gateForceX,
    timestamp: point.timestamp,
    force: point.gateForceX,
    velocity: point.gateAngleVel,
  }));

  return decimateData(converted, targetPoints);
}

/**
 * Decimate power output data for charts
 */
export function decimatePowerData(
  data: Array<{ interval: string; [key: string]: any }>,
  targetPoints: number = 1000
): Array<{ interval: string; [key: string]: any }> {
  // For interval data, we typically don't need decimation as it's already aggregated
  // But we can sample if there are too many intervals
  if (data.length <= targetPoints) {
    return data;
  }

  const step = Math.ceil(data.length / targetPoints);
  return data.filter((_, index) => index % step === 0);
}

/**
 * Performance monitoring for decimation operations
 */
export function measureDecimation<T>(
  operation: () => T,
  originalLength: number,
  targetPoints: number
): { result: T; duration: number; compressionRatio: number } {
  const start = performance.now();
  const result = operation();
  const duration = performance.now() - start;

  const compressionRatio = originalLength / targetPoints;

  // Performance metrics: ${originalLength} → ${targetPoints} points (${compressionRatio.toFixed(2)}x compression) in ${duration.toFixed(2)}ms

  return { result, duration, compressionRatio };
}
